/**
 * VoiceNote — record + playback components for 30s voice notes in chat.
 *
 *   - VoiceRecorderButton: mic-icon button in the chat input area. Press to
 *     start, press again to stop & send. 30s cap enforced.
 *   - VoiceNotePlayer: renders inside MessageBubble when message.voice exists.
 *     Lazy-fetches the audio from the server on first play to save bandwidth.
 */

import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Play, Pause, Loader2, X, AlertTriangle } from 'lucide-react';
import { sendVoiceNote, getChatVoice } from '../services/api';

const MAX_DURATION_MS = 30_000;

// ─── Recorder (lives inside the chat input) ───────────────────────────────

export function VoiceRecorderButton({ roomId, onSent, onError, disabled }) {
  const [state, setState] = useState('idle'); // idle | recording | uploading | error
  const [elapsed, setElapsed] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const tickRef = useRef(null);
  const stopTimerRef = useRef(null);

  async function startRecording() {
    setErrorMsg(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setErrorMsg('Voice notes need microphone access — your browser does not support it.');
      setState('error');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
                 : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
                 : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 64000 } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        try {
          stream.getTracks().forEach(t => t.stop());
          const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
          if (blob.size === 0) throw new Error('Empty recording');
          const dur = Date.now() - startTsRef.current;
          await uploadRecording(blob, dur);
        } catch (err) {
          setErrorMsg(err.message || 'Recording failed');
          setState('error');
          if (onError) onError(err.message);
        }
      };
      recorder.start();
      startTsRef.current = Date.now();
      setElapsed(0);
      setState('recording');
      tickRef.current = setInterval(() => setElapsed(Date.now() - startTsRef.current), 100);
      // Auto-stop at 30s
      stopTimerRef.current = setTimeout(() => stopRecording(), MAX_DURATION_MS);
    } catch (err) {
      setErrorMsg(err.message?.includes('Permission') ? 'Microphone permission denied' : (err.message || 'Could not start recording'));
      setState('error');
    }
  }

  function stopRecording() {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null; }
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') {
      setState('uploading');
      r.stop();
    } else {
      setState('idle');
    }
  }

  function cancelRecording() {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null; }
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') {
      r.ondataavailable = null;
      r.onstop = () => { r.stream?.getTracks().forEach(t => t.stop()); };
      r.stop();
    }
    chunksRef.current = [];
    setElapsed(0);
    setState('idle');
  }

  async function uploadRecording(blob, durationMs) {
    const dur = Math.min(durationMs, MAX_DURATION_MS);
    // Convert blob → base64 data URL
    const reader = new FileReader();
    const dataUrl = await new Promise((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    if (dataUrl.length > 600_000) {
      throw new Error('Voice note too large (>500KB)');
    }
    await sendVoiceNote(roomId, dataUrl, dur);
    setState('idle');
    setElapsed(0);
    if (onSent) onSent();
  }

  useEffect(() => () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
  }, []);

  if (state === 'recording') {
    const secs = (elapsed / 1000).toFixed(1);
    const pct = Math.min(100, (elapsed / MAX_DURATION_MS) * 100);
    return (
      <div className="flex items-center gap-1.5 bg-red-500/15 border border-red-500/30 rounded-xl px-2 py-1">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-xs text-red-300 font-mono">{secs}s</span>
        <div className="w-12 h-1 bg-red-500/20 rounded-full overflow-hidden">
          <div className="h-full bg-red-400 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <button onClick={cancelRecording} className="p-1 text-red-400 hover:text-red-300" title="Cancel">
          <X className="w-3.5 h-3.5" />
        </button>
        <button onClick={stopRecording} className="p-1 bg-red-500 rounded-md text-white hover:bg-red-400" title="Stop & send">
          <Square className="w-3 h-3" />
        </button>
      </div>
    );
  }

  if (state === 'uploading') {
    return (
      <button disabled className="p-1.5 rounded-lg text-emerald-400 flex-shrink-0">
        <Loader2 className="w-5 h-5 animate-spin" />
      </button>
    );
  }

  if (state === 'error') {
    return (
      <button onClick={() => { setState('idle'); setErrorMsg(null); }} title={errorMsg}
        className="p-1.5 rounded-lg text-red-400 hover:bg-dark-700 transition-all flex-shrink-0">
        <AlertTriangle className="w-5 h-5" />
      </button>
    );
  }

  return (
    <button onClick={startRecording} disabled={disabled}
      title="Record a voice note (30s max)"
      className="p-1.5 rounded-lg text-gray-500 hover:text-emerald-400 hover:bg-dark-700 transition-all flex-shrink-0 disabled:opacity-40">
      <Mic className="w-5 h-5" />
    </button>
  );
}

// ─── Player (inline in MessageBubble) ──────────────────────────────────────

export function VoiceNotePlayer({ voice, roomId }) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const audioRef = useRef(null);

  const durationSec = ((voice?.durationMs || 0) / 1000).toFixed(1);
  const totalSec = (voice?.durationMs || 0) / 1000;

  async function ensureLoaded() {
    if (audioUrl) return audioUrl;
    setLoading(true);
    try {
      const d = await getChatVoice(voice.audioId, roomId);
      setAudioUrl(d?.audio || null);
      return d?.audio;
    } catch (e) {
      setError('Could not load voice note');
    } finally {
      setLoading(false);
    }
  }

  async function togglePlay() {
    if (playing) {
      audioRef.current?.pause();
      return;
    }
    const src = await ensureLoaded();
    if (!src) return;
    if (audioRef.current) {
      audioRef.current.play().catch(() => setError('Playback failed'));
    }
  }

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnd = () => { setPlaying(false); setPosition(0); };
    const onTime = () => setPosition(a.currentTime);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnd);
    a.addEventListener('timeupdate', onTime);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('timeupdate', onTime);
    };
  }, [audioUrl]);

  const pct = totalSec > 0 ? Math.min(100, (position / totalSec) * 100) : 0;

  return (
    <div className="mt-2 bg-dark-700/40 border border-dark-700/50 rounded-xl px-3 py-2 flex items-center gap-3 max-w-xs">
      <button onClick={togglePlay} disabled={loading}
        className="w-9 h-9 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-dark-900 flex items-center justify-center flex-shrink-0">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 mb-1">
          <Mic className="w-3 h-3 text-gray-500" />
          <span className="text-[10px] text-gray-500">Voice note</span>
          <span className="text-[10px] text-gray-600 font-mono ml-auto">
            {playing || position > 0 ? `${position.toFixed(1)}s / ${durationSec}s` : `${durationSec}s`}
          </span>
        </div>
        <div className="h-1 bg-dark-600 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-400 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="auto" />}
      {error && <span className="text-[10px] text-red-400" title={error}>⚠</span>}
    </div>
  );
}
